<!--
  =====================================================================
  PRODUCTS CATEGORY PAGE - PRODUCT CATEGORY DETAIL
  =====================================================================
  
  Displays all products within a specific category with grid layout.
  
  FEATURES:
  - Loads product data for a specific macro-area and category
  - Displays category title, product cards grid, and description
  - Supports different color schemes for category styling
  - Dynamic link generation with /cassa/ prefix
  - Shows back button to products home page
  - Handles invalid categories with redirect to home
  
  URL PATTERN: /cassa/prodotti/:macroArea/:category
  EXAMPLE: /cassa/prodotti/famiglie-privati/conti-correnti
  
  =====================================================================
-->

<template>
  <div>
    <SiteHeader />

    <main class="products-category">
      <div class="category-back-top">
        <a href="/cassa/prodotti" class="back-to-products">
          <span class="arrow">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 10L12 15L17 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
          <span class="label">Torna ai prodotti</span>
        </a>
      </div>

      <div v-if="categoryData" class="category-content">
        <h1 class="page-title">{{ categoryData?.title }}</h1>
        
        <section :class="['category-grid', `category-grid--${categoryData?.colorScheme}`]">
          <ProductCategoryCard
            v-for="card in categoryData?.cards"
            :key="card.slug"
            :card="card"
            :colorScheme="categoryData?.colorScheme"
            :macroArea="macroArea"
            :category="category"
          />
        </section>

        <div 
          v-if="categoryData?.description" 
          class="page-description"
          v-html="sanitizeHtml(categoryData.description)"
        ></div>


      </div>
    </main>

    <SiteFooter />
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import { state, loadPageContent } from '@/store/index.js'
import { sanitizeHtmlRegex } from '@/utils/sanitizeHtml'

// ───────────────────────────────────────────────────────────────────
// SHARED LAYOUT COMPONENTS
// ───────────────────────────────────────────────────────────────────
import SiteHeader from '@/components/sections/shared/SiteHeader.vue'
import SiteFooter from '@/components/sections/shared/SiteFooter.vue'

// ───────────────────────────────────────────────────────────────────
// PRODUCT-SPECIFIC COMPONENTS
// ───────────────────────────────────────────────────────────────────
import ProductCategoryCard from '@/components/sections/prodotti/ProductCategoryCard.vue'

// ───────────────────────────────────────────────────────────────────
// PAGE STATE
// ───────────────────────────────────────────────────────────────────

/** Current category data */
const categoryData = ref(null)

/** Macro-area from URL path (e.g., 'famiglie-privati') */
const macroArea = ref(null)

/** Category from URL path (e.g., 'conti-correnti') */
const category = ref(null)

// ───────────────────────────────────────────────────────────────────
// UTILITIES
// ───────────────────────────────────────────────────────────────────

/**
 * Sanitize HTML content to prevent XSS attacks
 * @param {string} html - HTML string to sanitize
 * @returns {string} Sanitized HTML
 */
function sanitizeHtml(html) {
  return sanitizeHtmlRegex(html || '')
}

// ───────────────────────────────────────────────────────────────────
// LIFECYCLE HOOKS
// ───────────────────────────────────────────────────────────────────

/**
 * Load category data on component mount
 * 
 * Steps:
 * 1. Extract macro-area and category from URL path
 * 2. Skip 'cassa' prefix if present
 * 3. Redirect if trying to access 'links' macro-area
 * 4. Load products data if not already in store
 * 5. Retrieve category data from store
 */
onMounted(async () => {
  const pathSegments = window.location.pathname.split('/').filter(Boolean)
  
  // Normalize path: remove 'cassa' prefix
  let segments = pathSegments
  if (segments[0] === 'cassa') {
    segments = segments.slice(1)
  }
  
  // Extract parameters from path
  macroArea.value = segments[1]
  category.value = segments[2]
  
  // Prevent direct access to links macro-area
  if (macroArea.value === 'links') {
    window.location.href = '/'
    return
  }
  
  // Load products if not in store
  if (!state.categorie || Object.keys(state.categorie).length === 0) {
    await loadPageContent('prodotti')
  }
  
  // Get category data from store
  categoryData.value = state.categorie?.[macroArea.value]?.[category.value]
})
</script>

<style scoped lang="scss">
@import "@/assets/styles/partials/variables";
@import "@/assets/styles/partials/compat";
@import "@/assets/styles/partials/typography";

/* ------------------------------------------------------------------
   Back button in top-left
------------------------------------------------------------------ */
.category-back-top {
  position: fixed;
  top: calc(var(--header-total-height) + 1.2rem);
  left: 1rem;
  z-index: 150;

  .back-to-products {
    font-size: clamp(0.95rem, 2.5vw, 1.15rem);
    font-weight: 600;
    color: $clr-brand-dark;
    text-decoration: none;
    transition: all 0.3s ease;
    display: inline-flex;
    align-items: center;
    @include flex-gap(0.35rem);
    line-height: 1;
    padding: 0.6rem 0;

    @media (hover: hover) {
      &:hover {
        color: $clr-tertiary;
        transform: scale(1.05);
      }

      &:hover .arrow {
        transform: rotate(-90deg);
      }
    }

    .label {
      display: inline-block;
      white-space: nowrap;
      transition: opacity 0.25s ease, transform 0.25s ease;
      will-change: opacity, transform;
    }

    @media (max-width: 1700px) {
      @include flex-gap(0);

      .label {
        opacity: 0;
        transform: translateX(-6px);
        pointer-events: none;
      }
    }
  }

  @media (max-width: 1300px) {
    top: calc(var(--header-total-height) + 1.2rem);
    left: 1rem;
  }

  @media (max-width: 920px) { 
    display: none; 
  }

  @media (max-width: 768px) {
    left: 0;
  }
}

/* Arrow icon */
.arrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1em;
  height: 1em;
  transition: transform 0.3s ease;
  transform: rotate(90deg);

  svg {
    width: 100%;
    height: 100%;
    display: block;
  }
}

/* ------------------------------------------------------------------
   Page container and content
------------------------------------------------------------------ */
.products-category {
  padding: $space-4 0;
  background: $clr-bg-page;
  
  .category-content {
    max-width: $container-max-width;
    margin: 0 auto;
    padding: 0 $container-padding-x;
  }
  
  /* Page title */
  .page-title {
    font-size: $font-title;
    font-weight: 300;
    text-align: center;
    margin-bottom: $space-3;
    color: $text-color;
  }
  
  /* Product cards grid */
  .category-grid {
    display: grid;
    gap: $space-2;
    margin-bottom: $space-3;
    grid-template-columns: repeat(2, 1fr);
  }
  
  /* Category description */
  .page-description {
    font-size: $font-base;
    color: $clr-brand-gray;
    line-height: $line-height-base;
    max-width: 1200px;
    margin: 0 auto $space-3 auto;
    
    :deep(p) {
      margin-bottom: $paragraph-spacing;
      color: $clr-brand-gray;
    }
    
    :deep(ul),
    :deep(ol) {
      margin: $list-spacing 0;
      padding-left: $list-indent;
    }
    
    :deep(li) {
      margin-bottom: $space-0-75;
    }
    
    :deep(strong) {
      font-weight: 700;
      color: $clr-brand-gray;
    }
    
    :deep(em) {
      font-style: italic;
    }

    :deep(a) {
      color: $clr-tertiary;
      text-decoration: none;
    }

    :deep(h3) {
      font-size: $font-h4;
      font-weight: 600;
      margin: $space-2 0 $space-1 0;
      color: $clr-brand-gray;
    }
    
    :deep(table) {
      width: 100%;
      border-collapse: collapse;
      margin: $space-1-5 0;
      
      td, th {
        padding: $space-0-75;
        border: 1px solid $clr-border;
        text-align: left;
      }
      
      th {
        background-color: $clr-bg-light;
        font-weight: 600;
      }
    }
  }
}

/* ------------------------------------------------------------------
   Responsive - Medium displays (≤1300px)
------------------------------------------------------------------ */
@media (max-width: 1300px) {
  .products-category {
    padding: $space-3-5 0;
    
    .page-title {
      font-size: $font-title-lg;
      margin-bottom: $space-2-5;
    }
    
    .page-description {
      font-size: $font-base-lg;
      margin-bottom: $space-2-5;
    }
    
    .category-grid {
      gap: $space-1-75;
      margin-bottom: $space-2-5;
      grid-template-columns: repeat(2, 1fr);
    }
  }
}

/* ------------------------------------------------------------------
   Responsive - Tablet (≤920px)
   - Switch to single column grid
   - Reduce font sizes and padding
------------------------------------------------------------------ */
@media (max-width: 920px) {
  .products-category {
    padding: $space-3 0;
    
    .category-content {
      padding: 0 $container-padding-x-mob;
    }
    
    .page-title {
      font-size: $font-title-sm;
      margin-bottom: $space-2;
    }
    
    .page-description {
      font-size: $font-base-sm;
      margin-bottom: $space-2;
    }
    
    .category-grid {
      gap: $space-1-5;
      margin-bottom: $space-2;
      grid-template-columns: 1fr;
    }
  }
}

/* ------------------------------------------------------------------
   Responsive - Mobile (≤480px)
------------------------------------------------------------------ */
@media (max-width: 480px) {
  .products-category {
    padding: $space-2-5 0;
    
    .page-title {
      font-size: $font-title-xs;
      margin-bottom: $space-1-5;
    }
    
    .page-description {
      font-size: $font-base-xs;
      margin-bottom: $space-1-5;
    }
    
    .category-grid {
      gap: $space-1-25;
      margin-bottom: $space-1-5;
      grid-template-columns: 1fr;
    }
  }
}

/* ------------------------------------------------------------------
   Responsive - Very small mobile (≤360px)
------------------------------------------------------------------ */
@media (max-width: 360px) {
  .products-category {
    padding: $space-2 0;
    
    .page-title {
      font-size: $font-title-xxs;
    }
    
    .page-description {
      font-size: $font-base-xxs;
      margin-bottom: $space-1;
    }
    
    .category-grid {
      gap: $space-1;
      margin-bottom: $space-1;
    }
  }
}

/* ──────────────────────────────────────────────────────────────
   ALLINEAMENTO AL PARAGRAFO
   titolo e contenuto partono dalla stessa x di "chi siamo":
   44px a 390, 39 a 360. Classe ripetuta per battere le regole annidate.
   ────────────────────────────────────────────────────────────── */
@media (max-width: 480px) {
  .page-title.page-title.page-title {
    text-align: left;
    margin-left: 20px;
  }
}

@media (max-width: 360px) {
  .page-title.page-title.page-title {
    text-align: left;
    margin-left: 15px;
  }
}
</style>
